import React, { useState, useMemo } from 'react';

export default function LeanTopicPicker({ topics, selectedIds, onToggleGroup }) {
  const [query, setQuery] = useState('');
  const [expandedL1s, setExpandedL1s] = useState(new Set());

  const q = query.toLowerCase();

  const hierarchy = useMemo(() => {
    const byL1 = {};
    const filtered = q
      ? topics.filter(
          (t) =>
            (t.subject_area || '').toLowerCase().includes(q) ||
            (t.general_topic || '').toLowerCase().includes(q),
        )
      : topics;

    for (const t of filtered) {
      const l1 = t.subject_area || 'Other';
      const l2 = t.general_topic || 'General';
      if (!byL1[l1]) byL1[l1] = {};
      if (!byL1[l1][l2]) byL1[l1][l2] = { l3Ids: [], count: 0 };
      byL1[l1][l2].l3Ids.push(t.topic_id);
      byL1[l1][l2].count += t.question_count;
    }
    return byL1;
  }, [topics, q]);

  // Auto-expand all L1s when searching
  const effectiveExpanded = useMemo(
    () => (q ? new Set(Object.keys(hierarchy)) : expandedL1s),
    [q, hierarchy, expandedL1s],
  );

  const toggleL1 = (l1) => {
    setExpandedL1s((prev) => {
      const next = new Set(prev);
      if (next.has(l1)) next.delete(l1);
      else next.add(l1);
      return next;
    });
  };

  const l1Entries = Object.entries(hierarchy);

  // Count distinct L2s (general_topic) that have at least one selected L3
  const selectedL2Count = useMemo(() => {
    if (!selectedIds || selectedIds.size === 0) return 0;
    const l2s = new Set();
    for (const t of topics) {
      if (selectedIds.has(t.topic_id)) {
        l2s.add(t.general_topic || t.subject_area || 'General');
      }
    }
    return l2s.size;
  }, [topics, selectedIds]);

  return (
    <div>
      <input
        type="text"
        placeholder="Search subjects..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="w-full border border-border-soft rounded-r-md px-sp-4 py-sp-3 text-sm font-sans mb-sp-4 outline-none focus:border-accent bg-bg-card text-text-primary placeholder:text-text-muted"
      />

      <div className="border border-border-soft rounded-r-lg overflow-hidden mb-sp-3 max-h-96 overflow-y-auto">
        {l1Entries.length === 0 ? (
          <div className="px-sp-4 py-sp-6 text-sm text-text-muted text-center">
            No subjects with questions found.
          </div>
        ) : (
          l1Entries.map(([l1, l2Map]) => {
            const isExpanded = effectiveExpanded.has(l1);
            const allL3Ids = Object.values(l2Map).flatMap((v) => v.l3Ids);
            const l1SelectedCount = allL3Ids.filter((id) => selectedIds && selectedIds.has(id)).length;
            const l1AllSelected = l1SelectedCount === allL3Ids.length && allL3Ids.length > 0;
            const l1Partial = l1SelectedCount > 0 && !l1AllSelected;

            return (
              <div key={l1}>
                {/* L1 subject header — click to expand/collapse */}
                <div
                  className="flex items-center gap-sp-3 px-sp-4 py-sp-3 bg-bg-surface border-b border-border-soft cursor-pointer hover:bg-bg-page transition-colors select-none"
                  onClick={() => toggleL1(l1)}
                >
                  <input
                    type="checkbox"
                    checked={l1AllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = l1Partial;
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleGroup(allL3Ids, !l1AllSelected)}
                    className="w-4 h-4 accent-accent cursor-pointer flex-shrink-0"
                  />
                  <span className="text-[15px] font-semibold text-text-primary flex-1">{l1}</span>
                  <span className="text-xs text-text-muted pr-sp-1">{isExpanded ? '▲' : '▼'}</span>
                </div>

                {/* L2 study unit rows */}
                {isExpanded &&
                  Object.entries(l2Map).map(([l2, { l3Ids, count }]) => {
                    const l2SelectedCount = l3Ids.filter(
                      (id) => selectedIds && selectedIds.has(id),
                    ).length;
                    const l2AllSelected = l2SelectedCount === l3Ids.length && l3Ids.length > 0;
                    const l2Partial = l2SelectedCount > 0 && !l2AllSelected;

                    return (
                      <label
                        key={l2}
                        className="flex items-center gap-sp-3 pl-sp-8 pr-sp-4 py-sp-3 border-b border-border-soft last:border-b-0 cursor-pointer hover:bg-bg-page transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={l2AllSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = l2Partial;
                          }}
                          onChange={() => onToggleGroup(l3Ids, !l2AllSelected)}
                          className="w-4 h-4 accent-accent cursor-pointer flex-shrink-0"
                        />
                        <span className="text-sm text-text-primary flex-1">{l2}</span>
                      </label>
                    );
                  })}
              </div>
            );
          })
        )}
      </div>

      {selectedL2Count > 0 && (
        <p className="text-sm text-text-muted mb-sp-2">
          {selectedL2Count} study unit{selectedL2Count === 1 ? '' : 's'} selected
        </p>
      )}
    </div>
  );
}
